import React, { useState, useEffect } from "react";
import "./MarkdownEditor.css";

const MarkdownEditor = () => {
  const [markdown, setMarkdown] = useState(`# Welcome to Markdown Editor

This is a **real-time** markdown editor with live preview.

## Features

- ✨ Live preview
- 📝 Syntax highlighting
- 📤 Export options
- 🎨 Beautiful styling

### Code Example

\`\`\`javascript
function greet(name) {
  return \`Hello, \${name}!\`;
}

console.log(greet("World"));
\`\`\`

### Lists

1. First item
2. Second item
   - Nested item
   - Another nested item
3. Third item

### Links and Images

[Visit our website](https://example.com)

![Alt text](https://via.placeholder.com/300x200)

### Blockquote

> This is a blockquote. It can contain multiple lines and will be styled appropriately.

### Table

| Feature | Status | Description |
|---------|--------|-------------|
| Live Preview | ✅ | Real-time rendering |
| Export | ✅ | Multiple formats |
| Syntax Highlight | ✅ | Code highlighting |

---

**Happy writing!** 🎉`);

  const [preview, setPreview] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    setPreview(renderMarkdown(markdown));
    setWordCount(markdown.trim().split(/\s+/).filter(word => word.length > 0).length);
    setCharCount(markdown.length);
  }, [markdown]);

  const renderMarkdown = (text) => {
    // Basic markdown rendering
    let html = text
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      
      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      
      // Code blocks
      .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      
      // Images
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />')
      
      // Blockquotes
      .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
      
      // Horizontal rule
      .replace(/^---$/gim, '<hr>')
      
      // Lists
      .replace(/^\* (.*$)/gim, '<li>$1</li>')
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
      
      // Line breaks
      .replace(/\n/g, '<br>');

    // Wrap consecutive list items in ul/ol
    html = html.replace(/(<li>.*<\/li>)/g, (match) => {
      const lines = match.split('<br>');
      const listItems = lines.filter(line => line.includes('<li>'));
      if (listItems.length > 0) {
        return `<ul>${listItems.join('')}</ul>`;
      }
      return match;
    });

    return html;
  };

  const insertMarkdown = (syntax) => {
    const textarea = document.getElementById('markdown-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = markdown.substring(start, end);
    
    let newText = '';
    let cursorOffset = 0;
    
    switch (syntax) {
      case 'bold':
        newText = `**${selectedText}**`;
        cursorOffset = 2;
        break;
      case 'italic':
        newText = `*${selectedText}*`;
        cursorOffset = 1;
        break;
      case 'code':
        newText = `\`${selectedText}\``;
        cursorOffset = 1;
        break;
      case 'link':
        newText = `[${selectedText}](url)`;
        cursorOffset = -3;
        break;
      case 'image':
        newText = `![${selectedText}](url)`;
        cursorOffset = -3;
        break;
      case 'h1':
        newText = `# ${selectedText}`;
        cursorOffset = 2;
        break;
      case 'h2':
        newText = `## ${selectedText}`;
        cursorOffset = 3;
        break;
      case 'h3':
        newText = `### ${selectedText}`;
        cursorOffset = 4;
        break;
      case 'quote':
        newText = `> ${selectedText}`;
        cursorOffset = 2;
        break;
      case 'list':
        newText = `- ${selectedText}`;
        cursorOffset = 2;
        break;
      case 'codeblock':
        newText = `\`\`\`\n${selectedText}\n\`\`\``;
        cursorOffset = 4;
        break;
      default:
        newText = selectedText;
    }
    
    const newMarkdown = markdown.substring(0, start) + newText + markdown.substring(end);
    setMarkdown(newMarkdown);
    
    // Set cursor position
    setTimeout(() => {
      const newCursorPos = start + newText.length + cursorOffset;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  const exportAsHTML = () => {
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Markdown Export</title>
    <style>
        body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            max-width: 800px;
            margin: 0 auto;
            padding: 2rem;
            line-height: 1.6;
            color: #1e293b;
            background: #ffffff;
        }
        h1, h2, h3 { color: #1e40af; margin-top: 2rem; margin-bottom: 1rem; }
        h1 { font-size: 2rem; border-bottom: 2px solid #e2e8f0; padding-bottom: 0.5rem; }
        h2 { font-size: 1.5rem; }
        h3 { font-size: 1.25rem; }
        code { background: #f1f5f9; padding: 0.2rem 0.4rem; border-radius: 0.25rem; font-family: 'Monaco', monospace; }
        pre { background: #f8fafc; padding: 1rem; border-radius: 0.5rem; overflow-x: auto; border: 1px solid #e2e8f0; }
        pre code { background: none; padding: 0; }
        blockquote { border-left: 4px solid #3b82f6; padding-left: 1rem; margin: 1rem 0; color: #64748b; }
        ul, ol { padding-left: 2rem; }
        li { margin: 0.5rem 0; }
        a { color: #3b82f6; text-decoration: none; }
        a:hover { text-decoration: underline; }
        img { max-width: 100%; height: auto; border-radius: 0.5rem; }
        hr { border: none; border-top: 2px solid #e2e8f0; margin: 2rem 0; }
        table { border-collapse: collapse; width: 100%; margin: 1rem 0; }
        th, td { border: 1px solid #e2e8f0; padding: 0.75rem; text-align: left; }
        th { background: #f8fafc; font-weight: 600; }
    </style>
</head>
<body>
    ${preview}
    <hr>
    <p><small>Exported from Texify Elite Markdown Editor on ${new Date().toLocaleString()}</small></p>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const link = document.createElement("a");
    link.download = "markdown-export.html";
    link.href = window.URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showNotification("HTML file exported successfully!", "success");
  };

  const exportAsPDF = () => {
    // Simple PDF export using browser print
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Markdown Export</title>
        <style>
          body { font-family: 'Inter', sans-serif; max-width: 800px; margin: 0 auto; padding: 2rem; line-height: 1.6; }
          h1, h2, h3 { color: #1e40af; }
          code { background: #f1f5f9; padding: 0.2rem 0.4rem; border-radius: 0.25rem; }
          pre { background: #f8fafc; padding: 1rem; border-radius: 0.5rem; overflow-x: auto; }
          blockquote { border-left: 4px solid #3b82f6; padding-left: 1rem; color: #64748b; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        ${preview}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    showNotification("PDF export initiated!", "success");
  };

  const showNotification = (message, type = "info") => {
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      border-radius: 0.5rem;
      color: white;
      font-weight: 500;
      z-index: 10000;
      animation: slideIn 0.3s ease-out;
      background: ${type === "success" ? "#10b981" : type === "error" ? "#ef4444" : "#3b82f6"};
    `;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = "slideOut 0.3s ease-out forwards";
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  return (
    <div className="markdown-editor">
      <div className="markdown-header">
        <h2>📝 Markdown Editor & Preview (Beta)</h2>
        <p>Write in Markdown with real-time preview and export options</p>
      </div>

      <div className="markdown-toolbar">
        <div className="toolbar-group">
          <button onClick={() => insertMarkdown('bold')} title="Bold">
            <strong>B</strong>
          </button>
          <button onClick={() => insertMarkdown('italic')} title="Italic">
            <em>I</em>
          </button>
          <button onClick={() => insertMarkdown('code')} title="Inline Code">
            <code>&lt;/&gt;</code>
          </button>
        </div>
        
        <div className="toolbar-group">
          <button onClick={() => insertMarkdown('h1')} title="Heading 1">
            H1
          </button>
          <button onClick={() => insertMarkdown('h2')} title="Heading 2">
            H2
          </button>
          <button onClick={() => insertMarkdown('h3')} title="Heading 3">
            H3
          </button>
        </div>
        
        <div className="toolbar-group">
          <button onClick={() => insertMarkdown('link')} title="Link">
            🔗
          </button>
          <button onClick={() => insertMarkdown('image')} title="Image">
            🖼️
          </button>
          <button onClick={() => insertMarkdown('quote')} title="Quote">
            💬
          </button>
        </div>
        
        <div className="toolbar-group">
          <button onClick={() => insertMarkdown('list')} title="List">
            📋
          </button>
          <button onClick={() => insertMarkdown('codeblock')} title="Code Block">
            📄
          </button>
        </div>

        <div className="toolbar-group">
          <button 
            onClick={() => setShowPreview(!showPreview)} 
            className={showPreview ? 'active' : ''}
            title="Toggle Preview"
          >
            👁️
          </button>
        </div>
      </div>

      <div className="markdown-stats">
        <span>Words: {wordCount}</span>
        <span>Characters: {charCount}</span>
      </div>

      <div className="markdown-container">
        <div className="editor-section">
          <div className="section-header">
            <h3>📝 Editor</h3>
          </div>
          <textarea
            id="markdown-editor"
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            className="markdown-textarea"
            placeholder="Start writing your Markdown here..."
          />
        </div>

        {showPreview && (
          <div className="preview-section">
            <div className="section-header">
              <h3>👁️ Preview</h3>
              <div className="export-buttons">
                <button onClick={exportAsHTML} className="export-btn">
                  🌐 HTML
                </button>
                <button onClick={exportAsPDF} className="export-btn">
                  📄 PDF
                </button>
              </div>
            </div>
            <div 
              className="markdown-preview"
              dangerouslySetInnerHTML={{ __html: preview }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MarkdownEditor;

